/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import debounce from 'lodash.debounce';
import {error} from '@ion/core/src/logger/logger';
import {useKeyPress} from '../../hooks/useKeyPress';
import {dlsColor} from '../../theme/theme';
import {isCurrentTheme} from '../../theme/useThemeContext';
import {
  getExponentialBuffer,
  getMetersInDegree
} from '../helpers/globeUtils';
import initializeDrawingTools from '../helpers/initializeDrawingTools';
import {
  getMapPoints,
  removeActiveFence
} from '../helpers/MapViewUtils';
import {DEFAULT_SYMBOL} from './constants';
import {
  isLegacyMarker,
  registerCurrentDrawingUpdates,
  startGeofenceDrawingSession
} from './drawingStyles';

// defines the prop definitions for the CreateRectangularBoundaries
export type CreateRectangularBoundariesProps = {
  map: __esri.Map,
  mapView: __esri.MapView,
  radius?: number;
  setDrawingComplete?: (val: boolean) => void;
  setFinalDrawingDetails?: (val: {rings: Array<any>}) => void;
  finishedDrawing?: boolean;
  isFMTracking?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  selectedMapPointRef?: {
    current: {
      longitude: number;
      latitude: number;
    }
  }
  cancelDrawing?: Function;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
};

/**
 * Provides Drawing shapes & boundaries functionality
 *
 * ```typescript
 *
 *    <CreateRectangularBoundaries map={map} mapView={mapView} />
 *
 * ```
 */
export const CreateRectangularBoundaries = ({
  map,
  mapView,
  setDrawingComplete,
  setFinalDrawingDetails,
  finishedDrawing,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  isFMTracking = false
}: CreateRectangularBoundariesProps) => {
  const deleteKey    = useKeyPress('Delete');
  const escapeKey    = useKeyPress('Escape');
  const drawActionRef = useRef(null);

  useEffect(() => {
    initializeDrawing();
    const endDrawingSession = isCurrentTheme() ? startGeofenceDrawingSession(mapViewEl) : null;
    return () => {
      endDrawingSession?.();
      mapViewEl?.classList?.remove('default-cursor');
      mapViewEl?.classList?.remove('crosshair');
      drawActionRef.current?.destroy?.();
    };
  }, []);

  useEffect(() => {
    return function() {
      if (geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.complete();
        sketchViewModelRef.current?.cancel();
        map.remove(geoFencesGraphicsLayerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deleteKey || escapeKey) {
      if (geoFencesGraphicsLayerRef.current) {
        // If the user is currently manipulating a drawing
        // graphic delete it and re-initialize the drawing tool
        removeActiveFence(map, [geoFencesGraphicsLayerRef, sketchViewModelRef]);
        cancelDrawing();
        setDrawingComplete(false);
        mapClickHandler?.remove();
      }
    }
  }, [deleteKey, escapeKey]);

  useEffect(() => {
    if (finishedDrawing) {
      const items = geoFencesGraphicsLayerRef.current?.graphics?.items;
      if (items?.length && items[0].geometry?.rings) {
        setFinalDrawingDetails(items[0].geometry.rings[0]);
      }
    };
  }, [finishedDrawing]);

  useEffect(() => {
    if (selectedMapPointRef?.current) {
      initializeDrawing(true);
    }
  }, [selectedMapPointRef?.current]);

  async function initializeDrawing(drawBySearch: boolean = false) {
    const [
      Draw,
      Graphic,
      GraphicsLayer,
      SketchViewModel
    ] = await initializeDrawingTools(isFMTracking);
    const isCurrent    = isCurrentTheme();
    const circleSymbol = {
      type    : 'simple-marker',
      style   : 'circle',
      size    : 8,
      color   : dlsColor('white'),
      outline : {
        color : dlsColor('skyBlue'),
        width : 2
      }
    };
    const polygonSymbol = {
      type    : 'simple-fill',
      color   : [150, 150, 150, 0.2],
      outline : {
        color : dlsColor('skyBlue'),
        width : 2
      }
    };
    if (drawBySearch && selectedMapPointRef?.current) {
      const {longitude, latitude} = selectedMapPointRef.current;
      const degrees               = getMetersInDegree(1);
      const newLatitude           = latitude + getExponentialBuffer(mapView.zoom) * degrees;
      const newLongitude          =
          (longitude + getExponentialBuffer(mapView.zoom) * degrees) /
          Math.cos(latitude * (Math.PI / 180));
      const {vertices}            = getMapPoints({
        mapView,
        newLatitude,
        newLongitude,
        mapPointLongitude : longitude,
        mapPointLatitude  : latitude
      });
      updateVertices({
        vertices : [
          vertices[0]
        ]
      });
    } else {
      geoFencesGraphicsLayerRef.current = new GraphicsLayer();
      map.layers.add(geoFencesGraphicsLayerRef.current);
      map.layers.reorder(geoFencesGraphicsLayerRef.current, 1);
      mapViewEl?.classList?.add('crosshair');
      mapViewEl?.classList?.remove('default-cursor');

      sketchViewModelRef.current = new SketchViewModel({
        view                 : mapView,
        layer                : geoFencesGraphicsLayerRef.current,
        updateOnGraphicClick : true,
        defaultUpdateOptions : {
          multipleSelectionEnabled : false,
          preserveAspectRatio      : true,
          enableRotation           : false,
          toggleToolOnClick        : true
        }
      });
      sketchViewModelRef.current.on(['update', 'undo', 'redo'], onGraphicUpdate);
      // the current theme restyles the reshape / transform handles, and
      // scales a side handle's side only ...
      registerCurrentDrawingUpdates(sketchViewModelRef.current, isCurrent);

      const draw: __esri.Draw = new Draw({
        view : mapView
      });

      // creates and returns an instance of PolygonDrawAction
      drawActionRef.current = draw.create('rectangle');

      // listen PolygonDrawAction events to give immediate visual feedback
      // to users as the Polygon is being drawn on the view.
      drawActionRef.current.on(
        [
          'vertex-add',
          'vertex-remove',
          'redo',
          'undo',
          'draw-complete'
        ],
        updateVertices
      );

      const viewClickHandler = mapView.on('immediate-click', function(event) {
        event.stopPropagation();
        // check if the sketch's state active if it is then that means
        // the graphic is already being updated, no action required.
        if (sketchViewModelRef.current?.state === 'active') {
          return;
        }
        mapView.hitTest(event).then(function(response) {
          const results = response.results;
          // Check if the new development graphic was clicked and pass
          // the graphic to sketchViewModel.update() with reshape tool.
          results.forEach(function(result) {
            if (result?.graphic?.layer === sketchViewModelRef.current?.layer) {
              sketchViewModelRef.current?.update([result.graphic], {tool: 'reshape'});
            }
          });
        });
      });
      setMapClickHandler(viewClickHandler);
    }

    function updateVertices(event) {
      if (event.vertices.length === 1) {
        createGraphic(event);
        setDrawingComplete(true);
      }
    }

    function createGraphic(event) {
      const vertices = event.vertices;
      geoFencesGraphicsLayerRef.current?.graphics.removeAll();

      const buffer = getExponentialBuffer(mapView.zoom);

      const [centerX, centerY] = vertices[0];
      const startX = centerX - buffer;
      const startY = centerY - buffer;
      const endX = startX + (2 * buffer);
      const endY = startY + (2 * buffer);

      const corners = [
        [startX, startY],
        [endX, startY],
        [endX, endY],
        [startX, endY],
        [startX, startY]
      ];

      const graphic = new Graphic({
        geometry : {
          type             : 'polygon',
          id               : 'square-polygon',
          rings            : corners,
          spatialReference : mapView.spatialReference
        },
        symbol : DEFAULT_SYMBOL()
      });

      geoFencesGraphicsLayerRef.current?.graphics.add(graphic);
      if (geoFencesGraphicsLayerRef.current?.graphics?.items?.length) {
        sketchViewModelRef.current?.update(
          [geoFencesGraphicsLayerRef.current.graphics.items[0]], {tool: 'transform'}
        );
      }
      mapViewEl?.classList.add('default-cursor');
      mapViewEl?.classList.remove('crosshair');
    }

    function onGraphicUpdate(event) {
      // the current theme keeps the transform handles after a move, so the
      // shape can still be resized ...
      if (
        event.toolEventInfo &&
        event.toolEventInfo.type === 'move-stop' &&
        !isCurrent
      ) {
        sketchViewModelRef.current.complete();
      }

      try {
        if (event.state === 'start' || event.state === 'active' || event.state === 'complete') {
          updateTransformToolLayersGraphicStyles();
        }
      } catch (err) {
        error(err);
      }
    }

    function updateTransformToolLayersGraphicStyles() {
      const transformToolLayer = map.layers.items[map.layers.length - 1];
      transformToolLayer.graphics.items.forEach((graphicsItem) => {
        if (isLegacyMarker(graphicsItem, isCurrent)) {
          graphicsItem.symbol = circleSymbol;
        } else if (graphicsItem.symbol.type === 'simple-fill') {
          graphicsItem.symbol = polygonSymbol;
        }
      });
    }
  }

  return null;
};

export default CreateRectangularBoundaries;
